import { DestroyRef, Directive, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EditOrderFacade } from '@zero/order/domain';
import { PushQuoteService } from '@zero/shared/ui-push';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { OrderData } from '@zero/shared/util-models';
import { filter } from 'rxjs';

@Directive()
export abstract class EditOrderBase implements OnDestroy, OnChanges {
  readonly #destroyRef = inject(DestroyRef);
  readonly #quotesService = inject(PushQuoteService);
  public readonly customerStateFacade = inject(CustomerStateFacade);
  public readonly editOrderFacade = inject(EditOrderFacade);

  @Input() public order!: OrderData;

  public quote?: number;
  public quoteTime?: string;

  @Output() public dismiss = new EventEmitter<void>();
  @Output() public cancelFinished = new EventEmitter<void>();

  constructor() {
    this.editOrderFacade.orderCancellationDone$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((isCancelled) => isCancelled && this.done());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    changes['order']?.currentValue && this.init();
  }

  public ngOnDestroy(): void {
    this.#quotesService.unsubscribe(this.order?.isin);
    this.editOrderFacade.resetState();
  }

  public abstract done(): void;
  public abstract close(): void;

  public init = (): void => {
    this.editOrderFacade.checkTradingTimes(this.order.isin);
    this.setQuote();
  };

  public setQuote() {
    this.#quotesService
      .subscribe(this.order?.isin)
      .pipe(
        filter((quote) => !!quote),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((quote) => {
        if (this.order) {
          const { direction } = this.order;
          const { ask, bid, time } = quote;
          this.quote = direction === 'buy' ? ask : bid;
          this.quoteTime = time;
        }
      });
  }
}
